// Park settings set by admins on /park (admin console), served by the backend at
// GET /api/settings: points percentage and opening hours per weekday. The app
// falls back to the defaults below whenever they can't be loaded.
import { useEffect, useState } from "react";
import { BACKEND_URL } from "../features/payments/vipps";

const DAYS = [1, 2, 3, 4, 5, 6, 0]; // Monday..Sunday, Date.getDay() numbers

export const DEFAULT_PARK_SETTINGS = {
  pointsPercent: 4,
  hours: DAYS.map((day) => ({
    day, open: "10:00", close: day === 0 || day === 6 ? "19:00" : "21:00", closed: false, reason: "",
  })),
};

export function todayHours(settings, date) {
  const day = date.getDay();
  return (settings?.hours || []).find((h) => h.day === day) || DEFAULT_PARK_SETTINGS.hours.find((h) => h.day === day);
}

// Seconds from `now` until today's closing time; 0 after closing or on a closed day.
export function secondsUntilClosing(settings, now) {
  const today = todayHours(settings, now);
  if (today.closed) return 0;
  const [hour, minute] = today.close.split(":").map(Number);
  const closing = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0, 0);
  return Math.max(0, Math.floor((closing - now) / 1000));
}

// Status for the home park card: open/before/after/closedDay and how far through
// today's opening hours we are (0..1) for its progress bar.
export function parkStatus(settings, now) {
  const today = todayHours(settings, now);
  const base = { open: today.open, close: today.close, reason: today.reason || "" };
  if (today.closed) return { state: "closedDay", progress: 0, ...base };
  const minutes = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return h * 60 + m; };
  const nowMin = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
  const openMin = minutes(today.open);
  const closeMin = minutes(today.close);
  if (nowMin < openMin) return { state: "before", progress: 0, ...base };
  if (nowMin >= closeMin) return { state: "after", progress: 1, ...base };
  return { state: "open", progress: (nowMin - openMin) / (closeMin - openMin), ...base };
}

// 4 % is the normal level; anything above it is shown as a campaign.
export const isPointsCampaign = (percent) => Number(percent) > DEFAULT_PARK_SETTINGS.pointsPercent;

const CACHE_MS = 60 * 1000;
// The last good settings are also kept on the phone, so after an app restart the
// app doesn't run on the defaults (e.g. cap a ticket at 21:00 instead of 21:30)
// while the backend answers - or while it can't be reached.
const STORAGE_KEY = "pws_park_settings";
let cached = null;
let cachedAt = 0;
let inflight = null;

const isValidParkSettings = (s) => Array.isArray(s?.hours) && s.hours.length === 7 && Number.isFinite(s?.pointsPercent);

function readStored() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return isValidParkSettings(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function initialParkSettings() {
  return cached || readStored() || DEFAULT_PARK_SETTINGS;
}

function loadParkSettings() {
  if (!inflight) {
    inflight = fetch(`${BACKEND_URL}/api/settings`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`settings ${res.status}`))))
      .then((settings) => {
        if (!isValidParkSettings(settings)) throw new Error("settings in an unexpected shape");
        cached = settings;
        cachedAt = Date.now();
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch { /* storage full or blocked */ }
        return settings;
      })
      .catch(() => initialParkSettings())
      .finally(() => { inflight = null; });
  }
  return inflight;
}

// Current park settings; refetched at most once a minute (a page refresh remounts
// the page and picks up new values).
export function useParkSettings() {
  const [settings, setSettings] = useState(initialParkSettings);
  useEffect(() => {
    let alive = true;
    if (!cached || Date.now() - cachedAt > CACHE_MS) {
      loadParkSettings().then((s) => { if (alive) setSettings(s); });
    }
    return () => { alive = false; };
  }, []);
  return settings;
}
